'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { SpartanProductFeature } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/types';
interface UseFeatureImageReadinessOptions {
  features: SpartanProductFeature[];
  selectedIndex: number;
  loop: boolean;
}
const getImageKey = (feature: SpartanProductFeature): string => [feature.image.mobile.src, feature.image.desktop.src].join('|');
const getPreviousIndex = (selectedIndex: number, featureCount: number, loop: boolean): number => {
  if (featureCount <= 1) return -1;
  if (selectedIndex > 0) return selectedIndex - 1;
  return loop ? featureCount - 1 : -1;
};
const getNextIndex = (selectedIndex: number, featureCount: number, loop: boolean): number => {
  if (featureCount <= 1) return -1;
  if (selectedIndex + 1 < featureCount) return selectedIndex + 1;
  return loop ? 0 : -1;
};
export const useFeatureImageReadiness = t0 => {
  const $ = _c(35);
  const {
    features,
    selectedIndex,
    loop
  } = t0;
  const [settledImageKeys, setSettledImageKeys] = useState(_temp);
  let canSelectFeature;
  let canSelectPrevious;
  let isFeatureReady;
  let nextIndex;
  let previousIndex;
  let t1;
  if ($[0] !== features || $[1] !== loop || $[2] !== selectedIndex || $[3] !== settledImageKeys) {
    previousIndex = getPreviousIndex(selectedIndex, features.length, loop);
    nextIndex = getNextIndex(selectedIndex, features.length, loop);
    let t2;
    if ($[10] !== features || $[11] !== settledImageKeys) {
      t2 = index => {
        const feature = features[index];
        if (!feature) {
          return false;
        }
        if (feature.video?.desktop.src) {
          return true;
        }
        return settledImageKeys.has(getImageKey(feature));
      };
      $[10] = features;
      $[11] = settledImageKeys;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    isFeatureReady = t2;
    let t3;
    if ($[13] !== isFeatureReady || $[14] !== selectedIndex) {
      t3 = index_0 => isFeatureReady(selectedIndex) && isFeatureReady(index_0);
      $[13] = isFeatureReady;
      $[14] = selectedIndex;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    canSelectFeature = t3;
    canSelectPrevious = previousIndex >= 0 && canSelectFeature(previousIndex);
    t1 = nextIndex >= 0 && canSelectFeature(nextIndex);
    $[0] = features;
    $[1] = loop;
    $[2] = selectedIndex;
    $[3] = settledImageKeys;
    $[4] = canSelectFeature;
    $[5] = canSelectPrevious;
    $[6] = isFeatureReady;
    $[7] = nextIndex;
    $[8] = previousIndex;
    $[9] = t1;
  } else {
    canSelectFeature = $[4];
    canSelectPrevious = $[5];
    isFeatureReady = $[6];
    nextIndex = $[7];
    previousIndex = $[8];
    t1 = $[9];
  }
  const canSelectNext = t1;
  let t2;
  if ($[16] !== canSelectNext || $[17] !== canSelectPrevious || $[18] !== features.length || $[19] !== isFeatureReady || $[20] !== nextIndex || $[21] !== previousIndex || $[22] !== selectedIndex) {
    t2 = features.length > 1 && isFeatureReady(selectedIndex) && (previousIndex < 0 || canSelectPrevious) && (nextIndex < 0 || canSelectNext);
    $[16] = canSelectNext;
    $[17] = canSelectPrevious;
    $[18] = features.length;
    $[19] = isFeatureReady;
    $[20] = nextIndex;
    $[21] = previousIndex;
    $[22] = selectedIndex;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  const canDrag = t2;
  const canDragRef = useRef(canDrag);
  let t3;
  let t4;
  if ($[24] !== canDrag) {
    t3 = () => {
      canDragRef.current = canDrag;
    };
    t4 = [canDrag];
    $[24] = canDrag;
    $[25] = t3;
    $[26] = t4;
  } else {
    t3 = $[25];
    t4 = $[26];
  }
  useLayoutEffect(t3, t4);
  let t5;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => canDragRef.current;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const watchDrag = t5;
  let t6;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = feature_0 => {
      if (feature_0.video?.desktop.src) {
        return;
      }
      const imageKey = getImageKey(feature_0);
      setSettledImageKeys(current => {
        if (current.has(imageKey)) {
          return current;
        }
        const next = new Set(current);
        next.add(imageKey);
        return next;
      });
    };
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const handleImageSettled = t6;
  let t7;
  if ($[29] !== canSelectFeature || $[30] !== canSelectNext || $[31] !== canSelectPrevious || $[32] !== nextIndex || $[33] !== previousIndex) {
    t7 = {
      previousIndex,
      nextIndex,
      canSelectFeature,
      canSelectPrevious,
      canSelectNext,
      watchDrag,
      handleImageSettled
    };
    $[29] = canSelectFeature;
    $[30] = canSelectNext;
    $[31] = canSelectPrevious;
    $[32] = nextIndex;
    $[33] = previousIndex;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  return t7;
};
function _temp() {
  return new Set();
}